import { matchPath, useLocation, type Location } from 'react-router-dom';
import { useIsDesktop } from '../hooks';

/**
 * Synthetic background used when a desktop deep link lands directly on
 * `/event/:id` (no `backgroundLocation` in the nav state, e.g. a bookmark or
 * a shared URL): the primary `<Routes>` tree draws `/esplora` so `EventPage`
 * never renders on desktop — the modal (`EventModal`) covers the screen instead.
 */
const SYNTHETIC_ESPLORA_BACKGROUND: Location = {
  pathname: '/esplora',
  search: '',
  hash: '',
  state: null,
  key: 'default',
};

/**
 * The location the primary `<Routes>` tree in `AppRoutes` renders against —
 * the single source of truth for "which page is behind the modal".
 *
 * Contract for `state.backgroundLocation` (moved verbatim out of `AppRoutes`,
 * which now consumes this hook instead of recomputing the rule):
 * - `backgroundLocation` is honored ONLY when `useIsDesktop() === true`; on
 *   mobile it is ignored and the foreground route renders as a full page.
 * - A desktop deep link to `/event/:id` (no background) gets the synthetic
 *   `/esplora` background so `EventPage` never renders on desktop.
 * - Otherwise: the current location.
 *
 * Why providers need this too: `UserProvider` sits UNDER `BrowserRouter` but
 * OUTSIDE `<Routes>`, so its `useLocation()` returns the raw browser location
 * — which changes when a modal opens/closes (`/eventi/x` → `/event/:id` →
 * back) even though the primary tree never moved. Deriving
 * `locationResolutionPending` from the raw location would re-arm the territory
 * gate on modal close with no `useTerritoryRouteResolution` in view to clear
 * it (its effect keys on the pathname the mounted page sees), leaving the
 * homepage galleries in a permanent skeleton state. Both `AppRoutes` and
 * `UserProvider` must therefore read the SAME location.
 */
export function usePrimaryRoutesLocation(): Location {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const state = location.state as { backgroundLocation?: Location } | null;
  // CRITICAL: backgroundLocation effective ONLY on desktop
  const backgroundLocation = (isDesktop && state?.backgroundLocation) || null;
  const isEventRoute = Boolean(matchPath('/event/:id', location.pathname));
  const isDesktopDeepLinkEvent = isDesktop && isEventRoute && !backgroundLocation;
  return isDesktopDeepLinkEvent ? SYNTHETIC_ESPLORA_BACKGROUND : backgroundLocation || location;
}
